import { useCallback, useEffect, useState } from "react";
import { useLang } from "../hooks/useLang";
import Icon from "./icons";
import {
  listDocuments,
  getDocument,
  documentDocxURL,
  type GeneratedDocMeta,
  type GeneratedDocDetail,
} from "../services/documents";

// GeneratedDocuments — kredi karşılığı üretilen IMMUTABLE dokümanlar. Sol menü
// "Oluşturulan Dokümanlar". Liste + read-only görünüm; edit YOK. DOCX snapshot'tan iner.
export default function GeneratedDocuments() {
  const { t, lang } = useLang();
  const [docs, setDocs] = useState<GeneratedDocMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await listDocuments();
      setDocs(r.documents || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Arka planda hazırlanan doküman varsa listeyi periyodik tazele (hazırlanıyor→tamamlandı).
  useEffect(() => {
    if (!docs.some((d) => d.status === "preparing")) return;
    const iv = setInterval(() => { void load(); }, 4000);
    return () => clearInterval(iv);
  }, [docs, load]);

  const fmtDate = (s: string) =>
    s ? new Date(s).toLocaleString(lang === "en" ? "en-GB" : "tr-TR") : "";

  if (openId != null) {
    return <DocumentView id={openId} onBack={() => setOpenId(null)} />;
  }

  return (
    <div className="settings">
      <h2>{t("docs_title")}</h2>
      <p className="hint">{t("docs_intro")}</p>
      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      {loading ? (
        <div className="hint">{t("docs_loading")}</div>
      ) : docs.length === 0 ? (
        <div className="empty" style={{ marginTop: 16 }}>
          <h3>{t("docs_empty_title")}</h3>
          <p>{t("docs_empty_body")}</p>
        </div>
      ) : (() => {
        const s = query.trim().toLowerCase();
        const rows = s
          ? docs.filter((d) => (d.product_name || "").toLowerCase().includes(s) || (d.document_code || "").toLowerCase().includes(s))
          : docs;
        return (
        <div className="data-table-card" style={{ marginTop: 14 }}>
          <div className="data-table-toolbar">
            <div className="data-search">
              <Icon name="search" size={15} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("docs_search_ph")} />
            </div>
            <span className="data-count">{rows.length} {t("docs_count_unit")}</span>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("docs_col_doc")}</th>
                  <th>{t("docs_col_class")}</th>
                  <th>{t("docs_col_code")}</th>
                  <th>{t("docs_col_status")}</th>
                  <th>{t("docs_col_date")}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const preparing = d.status === "preparing";
                  const name = d.product_name || `#${d.id}`;
                  return (
                    <tr
                      key={d.id}
                      className={preparing ? "" : "row-click"}
                      onClick={preparing ? undefined : () => setOpenId(d.id)}
                    >
                      <td>
                        <div className="dt-main">
                          <span className="dt-avatar">{name.charAt(0).toUpperCase()}</span>
                          <span className="dt-name">{name}</span>
                        </div>
                      </td>
                      <td>{d.risk_class ? <span className="badge neutral">{d.risk_class}</span> : <span className="dt-muted">—</span>}</td>
                      <td className="dt-muted dt-mono">{d.document_code || "—"}</td>
                      <td>
                        {preparing ? (
                          <span className="badge doc-preparing"><span className="spinner-sm" />{t("docs_status_preparing")}</span>
                        ) : (
                          <span className="badge ok">{t("docs_status_ready")}</span>
                        )}
                      </td>
                      <td className="dt-muted dt-nowrap">{fmtDate(d.created_at)}</td>
                      <td>
                        <div className="dt-actions">
                          {!preparing && (
                            <a
                              className="dt-icon-btn"
                              href={documentDocxURL(d.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              title={t("docs_download")}
                            >
                              <Icon name="file" size={15} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// DocumentView — tek dokümanın read-only SPR map görünümü + DOCX indir.
function DocumentView({ id, onBack }: { id: number; onBack: () => void }) {
  const { t } = useLang();
  const [doc, setDoc] = useState<GeneratedDocDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getDocument(id).then(setDoc).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [id]);

  if (err) return <div className="settings"><button className="btn-link" onClick={onBack}>← {t("docs_back")}</button><div className="error">{t("err_prefix")}: {err}</div></div>;
  if (!doc) return <div className="settings"><div className="hint">{t("docs_loading")}</div></div>;

  const decisions = doc.decisions || [];
  return (
    <div className="settings">
      <button className="btn-link" onClick={onBack}>← {t("docs_back")}</button>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>{doc.product_name || `#${doc.id}`}</h2>
          <p className="hint" style={{ margin: 0 }}>
            {doc.risk_class ? `${doc.risk_class} · ` : ""}{doc.document_code ? `${doc.document_code} · ` : ""}{doc.version || ""}
            {" · "}<span className="badge ok">{t("docs_immutable_badge")}</span>
          </p>
        </div>
        <a className="btn-pri" href={documentDocxURL(doc.id)} target="_blank" rel="noopener noreferrer">
          📄 {t("docs_download")}
        </a>
      </div>
      <p className="hint" style={{ marginTop: 8 }}>{t("docs_readonly_note")}</p>

      {doc.summary && (
        <div className="hint" style={{ marginTop: 6 }}>
          {t("docs_summary")}: {doc.summary.total} · ✔ {doc.summary.applicable} · — {doc.summary.not_applicable}
        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {decisions.map((d) => (
          <div key={d.spr} className="card" style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
              <strong>{d.spr} — {d.title}</strong>
              <span className={"badge " + (d.applicability === "applicable" ? "ok" : "off")}>
                {d.applicability === "applicable" ? t("docs_applicable") : t("docs_not_applicable")}
              </span>
            </div>
            {(d.generated_text || d.justification) && (
              <p style={{ margin: "8px 0 0", fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                {d.generated_text || d.justification}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
